import mammoth from "mammoth";

// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS IS HEURISTIC, NOT EXACT
//
// A .docx only tells us what's *actually* structured: real Heading styles,
// real bold/italic runs, real bullet lists, real images. Anything else — like
// "Strategy No. 3" meant to read as a section title, or "Game Changer Tip:"
// meant to read as a callout — is just plain text as far as the file format
// is concerned. There's no reliable way to know that was the author's intent
// without guessing from patterns.
//
// So this importer does its best with pattern matching, and the result is
// meant to open directly in blog-editor.js's block editor for a quick human
// pass — not to be auto-published untouched. That review step is exactly
// what the block editor is for.
// ─────────────────────────────────────────────────────────────────────────────

const LABELS = [
  { re: /^(Game\s*Changer\s*Tip|Pro\s*Tip|Tip)\s*:?\s*/i, variant: "tip" },
  { re: /^(Applicable\s*Strategy|Strategy|Note)\s*:?\s*/i, variant: "info" },
  { re: /^(Red\s*Flag|Warning|Caution)\s*:?\s*/i, variant: "warning" },
];

const HEADING_LINE = /^(Strategy\s*No\.?\s*\d+\.?|Chapter\s*\d+|Part\s*\d+|Step\s*\d+)\b/i;

// Converts a DOM node's children into our **bold**/*italic* inline markdown,
// so imported text renders through the exact same renderInline() as
// hand-typed content.
function nodeToInlineMarkdown(node) {
  let out = "";
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out += child.textContent;
    } else if (/^(STRONG|B)$/i.test(child.tagName)) {
      out += `**${nodeToInlineMarkdown(child)}**`;
    } else if (/^(EM|I)$/i.test(child.tagName)) {
      out += `*${nodeToInlineMarkdown(child)}*`;
    } else {
      out += nodeToInlineMarkdown(child);
    }
  });
  return out;
}

// Removes stray single asterisks some authors type by hand for emphasis
// (e.g. "*Applicable Strategy:") without touching real **bold** pairs.
function stripFakeAsterisks(text) {
  return text.replace(/\*(?!\*)([^*]*?)\*(?!\*)/g, (match, inner) => {
    // Only strip if it doesn't look like intentional *italic word* (single word);
    // real Word docs rarely use single-asterisk italics manually, so this is safe.
    return inner;
  }).replace(/(?<!\*)\*(?!\*)/g, "");
}

function classifyParagraph(rawText) {
  const text = stripFakeAsterisks(rawText).trim();
  if (!text) return [];

  // Whole paragraph is bold end-to-end → treat as a heading (this is how the
  // title and "Conclusion" line show up in real docx exports that skip
  // proper Heading styles but do use real bold).
  const wholeBold = rawText.trim().match(/^\*\*(.+)\*\*$/);
  if (wholeBold) {
    return [{ type: "heading", level: 2, content: stripFakeAsterisks(wholeBold[1]).trim() }];
  }

  if (HEADING_LINE.test(text)) {
    return [{ type: "heading", level: 2, content: text }];
  }

  // Find the first label anywhere in the paragraph and split: everything
  // before it becomes its own paragraph, the label's sentence becomes a
  // callout. Handles docs where a tip is appended mid-paragraph rather than
  // on its own line.
  for (const label of LABELS) {
    const idx = text.search(label.re);
    if (idx > -1) {
      const before = text.slice(0, idx).trim();
      const after = text.slice(idx).replace(label.re, "").trim();
      const blocks = [];
      if (before) blocks.push({ type: "paragraph", content: before });
      if (after) blocks.push({ type: "callout", variant: label.variant, content: after });
      if (blocks.length) return blocks;
    }
  }

  return [{ type: "paragraph", content: text }];
}

export async function importDocxToBlocks(file) {
  const arrayBuffer = await file.arrayBuffer();

  const result = await mammoth.convertToHtml(
    { arrayBuffer },
    {
      convertImage: mammoth.images.imgElement((image) =>
        image.read("base64").then((data) => ({
          src: `data:${image.contentType};base64,${data}`,
        }))
      ),
    }
  );

  const doc = new DOMParser().parseFromString(result.value, "text/html");
  const blocks = [];
  let guessedTitle = "";

  for (const el of Array.from(doc.body.children)) {
    const tag = el.tagName;

    if (/^H[1-6]$/.test(tag)) {
      const content = nodeToInlineMarkdown(el).trim();
      if (!guessedTitle) guessedTitle = content.replace(/\*/g, "");
      blocks.push({ type: "heading", level: tag === "H1" ? 2 : 3, content });
      continue;
    }

    if (tag === "UL" || tag === "OL") {
      const items = Array.from(el.children).map((li) => nodeToInlineMarkdown(li).trim()).filter(Boolean);
      if (items.length) blocks.push({ type: "list", style: tag === "OL" ? "numbered" : "bullet", items });
      continue;
    }

    if (tag === "BLOCKQUOTE") {
      const content = nodeToInlineMarkdown(el).trim();
      if (content) blocks.push({ type: "quote", content });
      continue;
    }

    // Real Word tables → dynamic table blocks. First row is treated as the
    // header row if it contains any <th>, otherwise as a data row.
    if (tag === "TABLE") {
      const rowEls = Array.from(el.querySelectorAll("tr"));
      if (rowEls.length) {
        const firstIsHeader = rowEls[0].querySelector("th") !== null;
        const headerCells = firstIsHeader
          ? Array.from(rowEls[0].children).map((c) => nodeToInlineMarkdown(c).trim())
          : [];
        const dataRows = (firstIsHeader ? rowEls.slice(1) : rowEls).map((tr) =>
          Array.from(tr.children).map((c) => nodeToInlineMarkdown(c).trim())
        );
        blocks.push({
          type: "table",
          headers: headerCells,
          rows: dataRows,
          caption: "",
        });
      }
      continue;
    }

    if (tag === "IMG" || (tag === "P" && el.children.length === 1 && el.children[0].tagName === "IMG")) {
      const img = tag === "IMG" ? el : el.children[0];
      blocks.push({ type: "image", src: img.getAttribute("src") || "", alt: img.getAttribute("alt") || "", caption: "", width: "100", align: "center" });
      continue;
    }

    if (tag === "P") {
      const raw = nodeToInlineMarkdown(el);
      const produced = classifyParagraph(raw);
      if (produced.length && !guessedTitle && produced[0].type === "heading") {
        guessedTitle = produced[0].content.replace(/\*/g, "");
      } else if (produced.length && !guessedTitle && produced[0].type === "paragraph" && blocks.length === 0) {
        // First real paragraph in the whole doc, before any heading was found —
        // decent fallback guess for a title if the doc never uses one.
        guessedTitle = produced[0].content.slice(0, 70).replace(/\*/g, "");
      }
      blocks.push(...produced);
      continue;
    }

    // Unknown top-level tag — grab its text rather than silently dropping content.
    const fallbackText = el.textContent.trim();
    if (fallbackText) blocks.push({ type: "paragraph", content: fallbackText });
  }

  return {
    blocks,
    guessedTitle,
    warnings: result.messages.map((m) => m.message),
  };
}
